import { type ChangeEvent, forwardRef, type TextareaHTMLAttributes, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  /** Podświetla ramkę jak `error`, ale bez renderowania tekstu — do sygnałów serwerowych, gdy treść komunikatu żyje gdzie indziej (np. w toaście). */
  invalid?: boolean;
  /** Stan sukcesu (zielona ramka) — walidacja potwierdzona, bez tekstu (analogicznie do `invalid`). */
  success?: boolean;
  /**
   * Licznik znaków „1850 / 2000" pod polem, po prawej (w jednym wierszu z podpowiedzią lub błędem,
   * jeśli są). Działa tylko przy ustawionym `maxLength` — bez niego (albo bez tego propsa) wygląd
   * i znaczniki pola zostają bez zmian. Przy wpisywaniu i wklejaniu nie da się przekroczyć limitu
   * (`maxLength` przeglądarki: nie da się wpisać więcej, a wklejony dłuższy tekst zostaje ucięty);
   * wartość ustawiona z kodu (`value` / `defaultValue`) może być dłuższa — licznik pokazuje wtedy jej
   * rzeczywistą długość, także ponad limit.
   *
   * Pole kontrolowane (`value`): licznik zawsze zgadza się z wartością. Pole niekontrolowane: licznik
   * śledzi tylko wpisywanie przez użytkownika (zdarzenie `onChange`) — zmiany wartości z kodu (przez
   * `ref`, reset formularza) go nie aktualizują; pole, któremu wartość zmienia się z kodu, prowadź
   * jako kontrolowane.
   *
   * Czytnik ekranu: widoczny „N / max" jest przed nim ukryty (`aria-hidden`), a pole wskazuje przez
   * `aria-describedby` osobny tekst ukryty wizualnie („Wpisano N z M znaków"); zmiany nie są
   * ogłaszane na bieżąco (bez `aria-live`).
   */
  showCount?: boolean;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    hint,
    error,
    invalid,
    success,
    showCount,
    id,
    required,
    className,
    containerClassName,
    rows = 4,
    value,
    defaultValue,
    maxLength,
    onChange,
    'aria-describedby': describedByProp,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation();
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const counterEnabled = Boolean(showCount) && maxLength !== undefined;
  const countId = counterEnabled ? `${inputId}-count` : undefined;
  const describedBy =
    [describedByProp, errorId, hintId, countId].filter(Boolean).join(' ') || undefined;
  const isError = Boolean(error || invalid);
  const isSuccess = !isError && Boolean(success);

  // Pole kontrolowane (`value`): długość z wartości. Niekontrolowane: długość z początkowego
  // `defaultValue`, potem śledzona w `onChange` (tylko wpisywanie użytkownika — zmiany z kodu nie
  // docierają). Bez licznika nic z tego nie jest używane.
  const [uncontrolledLength, setUncontrolledLength] = useState(() =>
    defaultValue === undefined ? 0 : String(defaultValue).length,
  );
  const currentLength = value === undefined ? uncontrolledLength : String(value).length;
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    if (value === undefined) setUncontrolledLength(event.target.value.length);
    onChange?.(event);
  };

  const message =
    hint && !error ? (
      <span id={hintId} className="text-xs text-text-muted">
        {hint}
      </span>
    ) : error ? (
      <span id={errorId} className="text-xs text-red-600" role="alert">
        {error}
      </span>
    ) : null;

  return (
    <div className={['flex flex-col gap-1', containerClassName ?? ''].join(' ')}>
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-text-secondary">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}
      <textarea
        ref={ref}
        id={inputId}
        rows={rows}
        required={required}
        value={value}
        defaultValue={defaultValue}
        maxLength={maxLength}
        onChange={counterEnabled ? handleChange : onChange}
        aria-invalid={isError ? true : undefined}
        aria-describedby={describedBy}
        className={[
          'w-full rounded-lg border bg-bg-primary text-text-primary placeholder:text-text-muted',
          'px-3 py-2 text-sm leading-[22px] transition-colors resize-y',
          'focus:outline-none focus:ring-[3px]',
          'read-only:cursor-default read-only:border-transparent read-only:bg-bg-tertiary',
          'disabled:cursor-not-allowed disabled:border-transparent disabled:bg-bg-secondary disabled:text-text-muted',
          isError
            ? 'border-[rgb(var(--color-error))] focus:border-[rgb(var(--color-error))] focus:ring-[rgb(var(--color-error))]'
            : isSuccess
              ? 'border-[rgb(var(--color-success))] focus:border-[rgb(var(--color-success))] focus:ring-[rgb(var(--color-success))]'
              : 'border-border-secondary hover:border-border-primary focus:border-accent-600 focus:ring-accent-600',
          className ?? '',
        ].join(' ')}
        {...rest}
      />
      {counterEnabled ? (
        <div className="flex items-start justify-between gap-2">
          {message}
          {/* Widoczny licznik jest ukryty przed czytnikiem; pole opisuje tekst `sr-only` obok. */}
          <span
            aria-hidden="true"
            className="ml-auto shrink-0 text-xs tabular-nums text-text-muted"
          >
            {currentLength} / {maxLength}
          </span>
          <span id={countId} className="sr-only">
            {t('common.textarea.charCountSr', { count: currentLength, max: maxLength })}
          </span>
        </div>
      ) : (
        message
      )}
    </div>
  );
});
